import { getEnvironmentGTMContainerId } from '@dvag/dfs-constant-config/gtm';
import { addInitListener, getContext, getToken } from '@dvag/dfs-orchestrator-client';
import { defineCustomElements as designSystemDefineElements } from '@dvag/design-system/loader';
import { defineCustomElements as defineDesignSystemIcons } from '@dvag/design-system-icons/loader';
import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';

import { App } from 'App';
import { getLocale } from 'locale/i18n';
import { appConfigurationInit } from '@dvag/dfs-constant-config';
import { getMonitoring } from './service/getMonitoring';

const htmlEl = document.documentElement || document.getElementsByTagName('html')[0];
htmlEl.lang = getLocale();

interface WindowExtended extends Window {
  dataLayer?: { ['gtm.start']: number; event: string }[];
}

// Google Tag Manager
((w: WindowExtended, d, s, l: 'dataLayer', i) => {
  const w1 = w;
  w1[l] = w1[l] || [];
  w1[l]?.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  const f = d.getElementsByTagName(s)[0];
  const j: Node & { async: boolean; src: string } = d.createElement(s) as HTMLElement & {
    async: boolean;
    src: string;
  };
  j.async = true;
  j.src = `https://www.googletagmanager.com/gtm.js?id=${i}`;
  if (f.parentNode) {
    f.parentNode.insertBefore(j, f);
  }
})(window, document, 'script', 'dataLayer', getEnvironmentGTMContainerId());
// End Google Tag Manager

const USERLANE_PROJECT_ID = 'vywl2';
const USERLANE_SCRIPT_SRC = 'https://cdn.userlane.com/userlane.js';

type UserlaneCommand = ((command: string, ...args: unknown[]) => void) & {
  q?: unknown[];
};

interface UserlaneWindow extends Window {
  Userlane?: UserlaneCommand;
  UserlaneCommandObject?: string;
}

const loadUserlane = () => {
  const userlaneWindow = window as UserlaneWindow;
  userlaneWindow.UserlaneCommandObject = 'Userlane';
  if (!userlaneWindow.Userlane) {
    const queued: UserlaneCommand = (...args: unknown[]) => {
      (queued.q ??= []).push(args);
    };
    userlaneWindow.Userlane = queued;
  }
  if (document.querySelector(`script[src="${USERLANE_SCRIPT_SRC}"]`)) {
    return;
  }
  const script = document.createElement('script');
  const firstScript = document.getElementsByTagName('script')[0];
  script.async = true;
  script.src = USERLANE_SCRIPT_SRC;
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  }
};

const initUserlane = () => {
  const { Userlane } = window as UserlaneWindow;
  const advisorId = getContext().user?.advisor?.id;
  if (Userlane && advisorId) {
    Userlane('identify', advisorId);
    Userlane('init', USERLANE_PROJECT_ID);
  }
};

loadUserlane();

let root: ReturnType<typeof ReactDOM.createRoot> | undefined;

addInitListener(() =>
  appConfigurationInit(() => getToken() ?? '')
    .finally(() => {
      const rootElement = document.getElementById('root');
      if (!rootElement) {
        throw new Error('Failed to find the root element');
      }
      if (!root) {
        root = ReactDOM.createRoot(rootElement);
      }
      root.render(
        <StrictMode>
          <App />
        </StrictMode>,
      );
      designSystemDefineElements();

      defineDesignSystemIcons();

      getMonitoring().initTelemetry();

      initUserlane();
    })
    .catch((err) => {
      getMonitoring().trackException(err);
    }),
);
